/**
 * useProposals Hook
 *
 * Manages proposal state including fetching, WebSocket updates, and mutations.
 * Provides all functionality needed for the proposal review UI.
 */

import { createSignal, createResource, Accessor } from "solid-js";
import { api } from "../api/client";
import type {
  Proposal,
  ProposalId,
  ProposedActionId,
  ProjectId,
  ApproveProposalRequest,
  BoardEvent,
  Message,
  ProposalActionResponse,
} from "../api/types";

/** Result from approve/reject operations */
export interface ProposalOperationResult {
  /** The updated proposal */
  proposal: Proposal;
  /** AI continuation message if conversation was linked */
  continuation_message?: Message;
}

/** Result type for useProposals hook */
export interface UseProposalsResult {
  /** All pending proposals for the project */
  proposals: Accessor<Proposal[] | undefined>;
  /** Loading state for proposals list */
  loading: Accessor<boolean>;
  /** Error from proposals fetch */
  error: Accessor<Error | undefined>;
  /** Currently selected proposal (with full details) */
  selectedProposal: Accessor<Proposal | undefined>;
  /** Loading state for selected proposal detail */
  selectedLoading: Accessor<boolean>;
  /** Error from selected proposal fetch */
  selectedError: Accessor<Error | undefined>;
  /** Number of pending proposals */
  pendingCount: Accessor<number>;
  /** Select a proposal to view details (null to close) */
  select: (id: ProposalId | null) => void;
  /** Approve proposal (optionally with partial action selection) */
  approve: (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => Promise<ProposalOperationResult>;
  /** Reject proposal */
  reject: (id: ProposalId) => Promise<ProposalOperationResult>;
  /** Refetch the proposals list */
  refetch: () => void;
  /** Handle WebSocket events for proposals */
  handleWebSocketEvent: (event: BoardEvent) => void;
}

/**
 * Hook for managing proposals in the UI.
 *
 * @param projectId - Accessor for the current project ID
 * @returns UseProposalsResult with proposals state and actions
 *
 * @example
 * ```tsx
 * const {
 *   proposals,
 *   pendingCount,
 *   select,
 *   selectedProposal,
 *   approve,
 *   reject
 * } = useProposals(() => projectId);
 *
 * // Show pending count in status bar
 * <span>{pendingCount()} pending</span>
 *
 * // Handle WebSocket events
 * useWebSocket({
 *   onMessage: handleWebSocketEvent
 * });
 * ```
 */
export function useProposals(
  projectId: Accessor<ProjectId | undefined>,
): UseProposalsResult {
  const [selectedId, setSelectedId] = createSignal<ProposalId | null>(null);

  // Fetch pending proposals list
  // Backend returns { proposals: Proposal[] } wrapper
  const [proposalsResource, { refetch, mutate: mutateProposals }] =
    createResource(projectId, async (id) => {
      const response = await api.get<{ proposals: Proposal[] }>(
        `/projects/${id}/proposals`,
      );
      return response.proposals;
    });

  const [selectedResource, { mutate: mutateSelected }] = createResource(
    selectedId,
    async (id) => {
      if (!id) return undefined;
      return api.get<Proposal>(`/proposals/${id}`);
    },
  );

  // Reading an errored resource throws, and this hook runs in AppShell on
  // every route — an unguarded read would send a failed background fetch for
  // the status-bar count into render and blank the whole app. Gating on
  // state === "ready" keeps this peripheral concern failing closed and local.
  const readyProposals = () =>
    proposalsResource.state === "ready" ? proposalsResource() : undefined;
  const readySelected = () =>
    selectedResource.state === "ready" ? selectedResource() : undefined;

  const pendingCount = () => {
    const proposals = readyProposals();
    if (!proposals) return 0;
    return proposals.filter((p) => p.status === "pending").length;
  };

  const select = (id: ProposalId | null) => {
    setSelectedId(id);
    // Clear the cached selected proposal when deselecting
    // (createResource doesn't refetch when source becomes falsy)
    if (id === null) {
      mutateSelected(undefined);
    }
  };

  const approve = async (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ): Promise<ProposalOperationResult> => {
    const body: ApproveProposalRequest = actionIds
      ? { action_ids: actionIds }
      : {};
    const result = await api.post<ProposalActionResponse>(
      `/proposals/${id}/approve`,
      body,
    );

    const { continuation_message, ...proposal } = result;

    mutateProposals((prev) => prev?.map((p) => (p.id === id ? proposal : p)));

    return { proposal, continuation_message };
  };

  const reject = async (id: ProposalId): Promise<ProposalOperationResult> => {
    const result = await api.post<ProposalActionResponse>(
      `/proposals/${id}/reject`,
      {},
    );

    const { continuation_message, ...proposal } = result;

    // Update local state - remove from list since it's no longer pending
    mutateProposals((prev) => prev?.filter((p) => p.id !== id));

    if (selectedId() === id) {
      setSelectedId(null);
    }

    return { proposal, continuation_message };
  };

  const handleWebSocketEvent = (event: BoardEvent) => {
    switch (event.type) {
      case "ProposalUpdated":
        if (
          event.payload.status === "rejected" ||
          event.payload.status === "expired"
        ) {
          mutateProposals((prev) =>
            prev?.filter((p) => p.id !== event.payload.proposal_id),
          );
          if (selectedId() === event.payload.proposal_id) {
            setSelectedId(null);
          }
        } else {
          refetch();
        }
        break;

      case "ProposalCompleted":
        if (
          event.payload.status === "executed" ||
          event.payload.status === "partially_executed"
        ) {
          mutateProposals((prev) =>
            prev?.filter((p) => p.id !== event.payload.proposal_id),
          );
        }
        break;
    }
  };

  return {
    proposals: readyProposals,
    loading: () => proposalsResource.loading,
    error: () => proposalsResource.error,
    selectedProposal: readySelected,
    selectedLoading: () => selectedResource.loading,
    selectedError: () => selectedResource.error,
    pendingCount,
    select,
    approve,
    reject,
    refetch,
    handleWebSocketEvent,
  };
}
